import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  ShieldCheck,
  MapPin,
  Camera,
  Database,
  Lock,
  CheckCircle2,
  XCircle,
  X,
  FileText,
  HelpCircle,
  Clock,
  Send
} from 'lucide-react';
import {
  PRIVACY_POLICY_VERSION,
  privacyHighlights,
  privacySections,
  savePrivacyPolicyConsent,
} from '../data/privacyPolicyData';

export default function PrivacyPolicyModal({
  open,
  onClose,
  isMandatory = false,
  userNik = '',
  onAgree = null,
}) {
  // Pilihan persetujuan: 'agree' | 'disagree' | null
  const [consentChoice, setConsentChoice] = useState(null);
  const [showFullDetails, setShowFullDetails] = useState(false);

  useEffect(() => {
    if (open) {
      // Selalu reset pilihan ke null ketika modal dibuka agar karyawan memilih secara sadar
      setConsentChoice(null);
      setShowFullDetails(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = () => {
    if (!consentChoice) {
      Swal.fire({
        icon: 'info',
        title: 'Pilihan Belum Ditentukan',
        text: 'Silakan pilih "Ya, Saya Menyetujui" atau "Tidak" terlebih dahulu sebelum menekan tombol Submit.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'btn btn-primary' },
      });
      return;
    }

    if (consentChoice === 'disagree') {
      if (onClose) onClose();
      Swal.fire({
        icon: 'warning',
        title: 'Validasi Dibatalkan',
        text: '❌ Anda memilih Tidak menyetujui. Validasi akun tidak dapat dilanjutkan tanpa persetujuan Kebijakan Privasi dan izin akses perangkat (GPS & Kamera).',
        confirmButtonText: 'Mengerti',
        customClass: { confirmButton: 'btn btn-primary' },
      });
      return;
    }

    if (consentChoice === 'agree') {
      savePrivacyPolicyConsent(userNik);
      if (onClose) onClose();
      if (onAgree) {
        onAgree();
      }
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'MapPin':
        return <MapPin size={18} />;
      case 'Camera':
        return <Camera size={18} />;
      case 'Clock':
        return <Clock size={18} />;
      case 'ShieldCheck':
        return <ShieldCheck size={18} />;
      case 'Database':
        return <Database size={18} />;
      default:
        return <ShieldCheck size={18} />;
    }
  };

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            height: '100dvh',
            zIndex: 999999,
            background: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            boxSizing: 'border-box',
          }}
          onClick={!isMandatory ? onClose : undefined}
        >
          <motion.div
            initial={{ y: 28, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            transition={{ type: 'spring', damping: 26, stiffness: 350 }}
            style={{
              width: '100%',
              maxWidth: 530,
              maxHeight: '92vh',
              maxHeight: '92dvh',
              background: '#ffffff',
              borderRadius: 24,
              boxShadow: '0 25px 60px rgba(15, 23, 42, 0.35)',
              border: '1px solid rgba(226, 232, 240, 0.9)',
              fontFamily: "'Segoe UI', Roboto, sans-serif",
              textAlign: 'left',
              boxSizing: 'border-box',
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '18px 20px 14px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.28)',
                  }}
                >
                  <Lock size={22} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 17,
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '-0.3px',
                      }}
                    >
                      Kebijakan Privasi
                    </h3>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#0284c7',
                        background: '#e0f2fe',
                        padding: '2px 6px',
                        borderRadius: 6,
                      }}
                    >
                      v{PRIVACY_POLICY_VERSION}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    PT Kalisha Utama Ghani — Konfirmasi Izin & Data
                  </div>
                </div>
              </div>

              {!isMandatory && (
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.color = '#0f172a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Scrollable Body */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px 20px',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {/* Box Pertanyaan ke Karyawan (Tampil saat validasi) */}
              {isMandatory && (
                <div
                  style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
                    border: '1.5px solid #93c5fd',
                    borderRadius: 14,
                    padding: '14px 16px',
                    marginBottom: 16,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <HelpCircle size={18} color="#0284c7" />
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Pertanyaan Persetujuan Karyawan
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: '#1e293b', lineHeight: 1.5, fontWeight: 600 }}>
                    Sebelum validasi {userNik ? `untuk NIK/Debitur [${userNik}] ` : ''}diproses, apakah Anda menyetujui Kebijakan Privasi serta pemberian izin akses perangkat di bawah ini?
                  </p>
                </div>
              )}

              {/* Pilihan Interaktif: Ya, Saya Menyetujui vs Tidak */}
              {isMandatory && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Pilih Keputusan Anda:
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Opsi 1: Ya, Saya Menyetujui */}
                    <div
                      onClick={() => setConsentChoice('agree')}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: consentChoice === 'agree' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                        background: consentChoice === 'agree' ? '#f0f9ff' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: consentChoice === 'agree' ? '0 2px 8px rgba(2, 132, 199, 0.15)' : 'none',
                      }}
                    >
                      <input
                        type="radio"
                        id="privacy-choice-agree"
                        name="privacy_consent_choice"
                        checked={consentChoice === 'agree'}
                        onChange={() => setConsentChoice('agree')}
                        style={{
                          marginTop: 3,
                          width: 18,
                          height: 18,
                          accentColor: '#0284c7',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <label htmlFor="privacy-choice-agree" style={{ cursor: 'pointer', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CheckCircle2 size={16} color="#0284c7" />
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                            Ya, saya menyetujui
                          </span>
                        </div>
                        <div style={{ fontSize: 11.5, color: '#475569', marginTop: 3, lineHeight: 1.4 }}>
                          Saya menyetujui Kebijakan Privasi serta memberikan izin akses lokasi GPS, pemantauan lokasi pasca absen masuk (30 mnt / 1 jam), kamera swafoto, dan identifikasi perangkat.
                        </div>
                      </label>
                    </div>

                    {/* Opsi 2: Tidak */}
                    <div
                      onClick={() => setConsentChoice('disagree')}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: consentChoice === 'disagree' ? '2px solid #ef4444' : '1px solid #cbd5e1',
                        background: consentChoice === 'disagree' ? '#fef2f2' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: consentChoice === 'disagree' ? '0 2px 8px rgba(239, 68, 68, 0.15)' : 'none',
                      }}
                    >
                      <input
                        type="radio"
                        id="privacy-choice-disagree"
                        name="privacy_consent_choice"
                        checked={consentChoice === 'disagree'}
                        onChange={() => setConsentChoice('disagree')}
                        style={{
                          marginTop: 3,
                          width: 18,
                          height: 18,
                          accentColor: '#ef4444',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <label htmlFor="privacy-choice-disagree" style={{ cursor: 'pointer', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <XCircle size={16} color="#ef4444" />
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#991b1b' }}>
                            Tidak
                          </span>
                        </div>
                        <div style={{ fontSize: 11.5, color: '#7f1d1d', marginTop: 3, lineHeight: 1.4 }}>
                          Saya tidak menyetujui. Proses validasi akun akan dibatalkan dan saya tidak dapat melakukan presensi melalui aplikasi.
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Highlights Cards Ringkasan */}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Ringkasan Izin Akses & Penggunaan Data
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                {privacyHighlights.map((hl, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: hl.bgColor,
                          color: hl.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {getIcon(hl.icon)}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                        {hl.title}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.35, paddingLeft: 36 }}>
                      {hl.desc}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tombol Lihat/Sembunyikan Pasal Lengkap */}
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowFullDetails(!showFullDetails)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0284c7',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <FileText size={14} />
                  <span>{showFullDetails ? 'Sembunyikan Pasal Lengkap' : 'Baca Rincian Pasal Kebijakan Lengkap (7 Pasal)'}</span>
                </button>
              </div>

              {/* Detailed Articles (Tampil jika di-expand atau mode viewing) */}
              {showFullDetails && (
                <div
                  style={{
                    background: '#fafafa',
                    border: '1px solid #eef2f6',
                    borderRadius: 14,
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    marginBottom: 10,
                  }}
                >
                  {privacySections.map((sec, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                        {sec.title}
                      </div>
                      <div>{sec.content}</div>
                      {sec.bullets && (
                        <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, color: '#475569' }}>
                          {sec.bullets.map((b, bIdx) => (
                            <li key={bIdx} style={{ marginBottom: 3 }}>
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with Submit Button */}
            <div
              style={{
                padding: '14px 20px 16px',
                borderTop: '1px solid #f1f5f9',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {isMandatory ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      padding: '12px 18px',
                      borderRadius: 12,
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      fontSize: 13.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Batal
                  </button>

                  <button
                    type="button"
                    disabled={!consentChoice}
                    onClick={handleSubmit}
                    style={{
                      flex: 1,
                      padding: '12px 18px',
                      borderRadius: 12,
                      background: !consentChoice
                        ? '#cbd5e1'
                        : consentChoice === 'agree'
                          ? 'linear-gradient(135deg, #0284c7, #0369a1)'
                          : 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: '#ffffff',
                      border: 'none',
                      fontSize: 13.5,
                      fontWeight: 700,
                      cursor: consentChoice ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      boxShadow: consentChoice === 'agree'
                        ? '0 4px 14px rgba(2, 132, 199, 0.35)'
                        : consentChoice === 'disagree'
                          ? '0 4px 14px rgba(239, 68, 68, 0.3)'
                          : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Send size={16} />
                    <span>
                      {consentChoice === 'agree'
                        ? 'Submit Persetujuan'
                        : consentChoice === 'disagree'
                          ? 'Submit (Batalkan Validasi)'
                          : 'Pilih Ya atau Tidak lalu Submit'}
                    </span>
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 11.5, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <CheckCircle2 size={16} />
                    <span>Kebijakan Privasi v{PRIVACY_POLICY_VERSION} (Dokumen Resmi)</span>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 10,
                      background: '#f1f5f9',
                      color: '#334155',
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                  >
                    Tutup
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
